/**
 * Calendar + email alerts — two columns: the financial calendar (upcoming
 * earnings dates) and the investor email-alerts sign-up (panel variant of the
 * shared `InvestorAlertsForm`).
 */

import AgntTextIcon from 'components/agnt-inc/agnt-text-icon';
import InvestorAlertsForm from 'components/agnt-inc/investor-alerts-form';

import styles from './calendar-alerts.module.scss';

import type { AlertsLink, CalendarAlerts as CalendarAlertsContent, CalendarEvent } from '../content';

// A financial calendar lists what's coming up — drop events whose date has
// passed (through the end of that day). Undated/TBD events are always kept.
function isPastEvent(event: CalendarEvent): boolean {
  if (!event.date) return false;
  return new Date(`${event.date}T23:59:59`).getTime() < Date.now();
}

const ICONS: Record<AlertsLink['icon'], JSX.Element> = {
  email: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <rect x="1" y="3" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <path d="M1 4l6 4 6-4" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  ),
  linkedin: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <rect x="1" y="1" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.3" />
      <path d="M4 6v4M4 4.5v.1M7 10V7.5C7 6.7 7.7 6 8.5 6S10 6.7 10 7.5V10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  ),
  careers: (
    <svg width="16" height="16" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <rect x="1" y="4" width="12" height="9" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5 4V3a2 2 0 014 0v1" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  ),
};

export default function CalendarAlerts({ content }: { content: CalendarAlertsContent }) {
  const { form } = content;
  const upcomingEvents = content.events.filter(event => !isPastEvent(event));
  return (
    <div className={styles.cal} id="alerts">
      <div className={styles.grid}>
        <div className={styles.left}>
          <div data-reveal>
            <div className={styles.eyebrow}>{content.scheduleEyebrow}</div>
            <h2 className={styles.heading}>{content.scheduleHeading}</h2>
            <div className={styles.events}>
              {upcomingEvents.length === 0 ? (
                <p className={styles.eventMeta}>No upcoming events scheduled.</p>
              ) : (
                upcomingEvents.map(event => (
                  <div key={event.title} className={styles.event}>
                    <div className={styles.eventDate}>
                      <div className={event.day.length > 2 ? styles.eventDayShort : styles.eventDay}>{event.day}</div>
                      <div className={styles.eventMonth}>{event.month}</div>
                    </div>
                    <div>
                      <div className={styles.eventTitle}>{event.title}</div>
                      {event.meta ? <div className={styles.eventMeta}>{event.meta}</div> : null}
                      {event.addToCalendar ? (
                        <a className={styles.addToCalendar} href={event.addToCalendar.href} target="_blank" rel="noopener noreferrer">
                          {event.addToCalendar.label}
                        </a>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className={styles.viewAll}>
              <AgntTextIcon href={content.viewAll.href} label={content.viewAll.label} />
            </div>
          </div>
        </div>
        <div className={styles.right}>
          <div data-reveal>
            <div className={styles.eyebrow}>{form.eyebrow}</div>
            <h2 className={styles.heading}>{form.heading}</h2>
            <p className={styles.body}>{form.body}</p>
            <InvestorAlertsForm variant="panel" />
            {form.links.length > 0 && (
              <div className={styles.wlinks}>
                {form.links.map(link => (
                  <a key={link.href} className={styles.wlink} href={link.href} target={link.href.startsWith('mailto:') ? undefined : '_blank'} rel={link.href.startsWith('mailto:') ? undefined : 'noopener noreferrer'}>
                    {ICONS[link.icon]}
                    {link.label}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
